/* eslint-disable jsx-a11y/anchor-is-valid */
import {useState} from 'react'
import * as Yup from 'yup'
import clsx from 'clsx'
import {Link, useNavigate} from 'react-router-dom'
import {useFormik} from 'formik'
import {get_user_permissions, getUserByToken, login} from '../core/_requests'
import {useAuth} from '../core/Auth'
import { toast } from 'react-toastify'
import {toAbsoluteUrl} from '../../../../_metronic/helpers'

const loginSchema = Yup.object().shape({
  email: Yup.string()
    .email('Wrong email format')
    .min(3, 'Minimum 3 symbols')
    .max(50, 'Maximum 50 symbols')
    .required('Email is required'),
  password: Yup.string()
    .min(3, 'Minimum 3 symbols')
    .max(50, 'Maximum 50 symbols')
    .required('Password is required'),
})

const initialValues = {
  email: '',
  password: '',
}

/*
  Formik+YUP+Typescript:
  https://jaredpalmer.com/formik/docs/tutorial#getfieldprops
  https://medium.com/@maurice.de.beijer/yup-validation-and-typescript-and-formik-6c342578a20e
*/

export function Login() {
  const [loading, setLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const {saveAuth, setCurrentUser, savePermission} = useAuth()
  const navigate = useNavigate()

  const formik = useFormik({
    initialValues,
    validationSchema: loginSchema,
    onSubmit: async (values, {setStatus, setSubmitting}) => {
      setLoading(true)
      console.log('Login form submitted')
      try {
        const {data: auth} = await login(values.email, values.password)
        console.log('Login API success:', auth)
        saveAuth(auth)
        
        // Check if auth.admin exists before accessing theme_mode
        if (auth.admin && auth.admin.theme_mode) {
          localStorage.setItem('kt_theme_mode_menu', auth.admin.theme_mode);
          localStorage.setItem('kt_theme_mode_value', auth.admin.theme_mode);
        }
        
        // Fetch user permissions after successful login
        try {
          const permissionsData = await get_user_permissions()
          console.log('Permissions API response:', permissionsData)
          
          if (permissionsData && permissionsData.data) {
            const rawPermissions = permissionsData.data
            const permissionsObject: Record<string, boolean> = {}

            if (Array.isArray(rawPermissions) && rawPermissions.length > 0) {
              if (typeof rawPermissions[0] === 'string') {
                rawPermissions.forEach((key: string) => {
                  permissionsObject[key] = true
                })
              } else if (typeof rawPermissions[0] === 'object') {
                rawPermissions.forEach((perm: any) => {
                  if (perm?.create) permissionsObject[perm.create] = true
                  if (perm?.view) permissionsObject[perm.view] = true
                  if (perm?.update) permissionsObject[perm.update] = true
                  if (perm?.delete) permissionsObject[perm.delete] = true
                  if (perm?.activity_log) permissionsObject[perm.activity_log] = true
                })
              }
            } else if (rawPermissions && typeof rawPermissions === 'object') {
              Object.entries(rawPermissions).forEach(([permissionKey, value]) => {
                if (typeof value === 'boolean') {
                  permissionsObject[permissionKey] = value
                } else if (typeof value === 'number') {
                  permissionsObject[permissionKey] = value === 1
                } else if (typeof value === 'string') {
                  const normalized = value.toLowerCase()
                  permissionsObject[permissionKey] =
                    normalized === 'true' || normalized === '1' || value === permissionKey
                } else {
                  permissionsObject[permissionKey] = Boolean(value)
                }
              })
            }
            
            savePermission(permissionsObject)
            console.log('Permissions loaded and stored:', permissionsObject)
          } else {
            savePermission({})
            console.warn('No permissions data received')
          }
        } catch (permissionsError: any) {
          console.error('Error fetching permissions:', permissionsError)
          // Set empty permissions if API fails, but don't block login
          savePermission({})
        }
        
        // Set current user from auth response
        setCurrentUser(auth.admin || {})
        console.log('User data set, redirecting to dashboard...')
        setLoading(false)
        setSubmitting(false)
        
        // Redirect to dashboard without full reload
        navigate('/dashboard', {replace: true})
      } catch (error: any) {
        console.error('Login error:', error)
        if (error.response) {
          toast.error(error.response.data.message, {
            position: 'top-right',
            autoClose: 3000,
          });
        }
        saveAuth(undefined)
        setStatus('The login details are incorrect')
        setSubmitting(false)
        setLoading(false)
      }
    },
  })

  return (
    <form
      className='form w-100'
      onSubmit={formik.handleSubmit}
      noValidate
      id='kt_login_signin_form'
    >
      {/* begin::Heading */}
      <div className='text-center mb-11'>
        <img
          alt='Digital Assets MS'
          src={toAbsoluteUrl('/media/logos/dams_dark_logo.png')}
          className='h-40px mb-5'
          onError={(e:any) => {
            e.target.style.display = 'none'
          }}
        />
        <h1 className='text-dark fw-bolder mb-3'>Sign In</h1>
      </div>
      {/* begin::Heading */}

      {/* begin::Login options */}
      {/* <div className='row g-3 mb-9'>
        <div className='col-md-6'>
          <a
            href='#'
            className='btn btn-flex btn-outline btn-text-gray-700 btn-active-color-primary bg-state-light flex-center text-nowrap w-100'
          >
            <img
              alt='Logo'
              src={toAbsoluteUrl('/media/svg/brand-logos/google-icon.svg')}
              className='h-15px me-3'
            />
            Sign in with Google
          </a>
        </div>
        <div className='col-md-6'>
          <a
            href='#'
            className='btn btn-flex btn-outline btn-text-gray-700 btn-active-color-primary bg-state-light flex-center text-nowrap w-100'
          >
            <img
              alt='Logo'
              src={toAbsoluteUrl('/media/svg/brand-logos/apple-black.svg')}
              className='theme-light-show h-15px me-3'
            />
            <img
              alt='Logo'
              src={toAbsoluteUrl('/media/svg/brand-logos/apple-black-dark.svg')}
              className='theme-dark-show h-15px me-3'
            />
            Sign in with Apple
          </a>
        </div>
      </div>
      <div className='separator separator-content my-14'>
        <span className='w-125px text-gray-500 fw-semibold fs-7'>Or with email</span>
      </div> */}
      {/* end::Separator */}

      {/* begin::Form group */}
      <div className='fv-row mb-8'>
        <label className='form-label fs-6 fw-bolder text-dark'>Email</label>
        <input
          placeholder='Email'
          {...formik.getFieldProps('email')}
          className={clsx(
            'form-control bg-transparent',
            {'is-invalid': formik.touched.email && formik.errors.email},
            {
              'is-valid': formik.touched.email && !formik.errors.email,
            }
          )}
          type='email'
          name='email'
          autoComplete='off'
        />
        {formik.touched.email && formik.errors.email && (
          <div className='fv-plugins-message-container'>
            <div className='fv-help-block'>
              <span role='alert'>{formik.errors.email}</span>
            </div>
          </div>
        )}
      </div>
      {/* end::Form group */}

      {/* begin::Form group */}
      <div className='fv-row mb-3'>
        <label className='form-label fw-bolder text-dark fs-6 mb-0'>Password</label>
        <div className='input-group'>
          <input
            type={showPassword ? 'text' : 'password'}
            placeholder='Password'
            autoComplete='off'
            {...formik.getFieldProps('password')}
            className={clsx(
              'form-control bg-transparent',
              {
                'is-invalid': formik.touched.password && formik.errors.password,
              },
              {
                'is-valid': formik.touched.password && !formik.errors.password,
              }
            )}
          />
          <span
            className={`input-group-text ${showPassword
              ? 'text-primary cursor-pointer border-0'
              : 'text-muted cursor-pointer border-0'
            }`}
            onClick={() => setShowPassword(!showPassword)}
            style={{ cursor: 'pointer' }}
          >
            {showPassword ? (
              <i className='bi bi-eye-slash-fill'></i>
            ) : (
              <i className='bi bi-eye-fill'></i>
            )}
          </span>
        </div>
        {formik.touched.password && formik.errors.password && (
          <div className='fv-plugins-message-container'>
            <div className='fv-help-block'>
              <span role='alert'>{formik.errors.password}</span>
            </div>
          </div>
        )}
      </div>
      {/* end::Form group */}

      {/* begin::Wrapper */}
      <div className='d-flex flex-stack flex-wrap gap-3 fs-base fw-semibold mb-8'>
        <div />

        {/* begin::Link */}
        <Link to='/auth/forgot-password' className='link-primary'>
          Forgot Password ?
        </Link>
        {/* end::Link */}
      </div>
      {/* end::Wrapper */}

      {/* begin::Action */}
      <div className='d-grid mb-10'>
        <button
          type='submit'
          id='kt_sign_in_submit'
          className='btn btn-primary'
          disabled={formik.isSubmitting || !formik.isValid}
        >
          {!loading && <span className='indicator-label'>Continue</span>}
          {loading && (
            <span className='indicator-progress' style={{display: 'block'}}>
              Please wait...
              <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
            </span>
          )}
        </button>
      </div>
      {/* end::Action */}
      {/* <div className='text-gray-500 text-center fw-semibold fs-6'>
        Not a Member yet?{' '}
        <Link to='/auth/registration' className='link-primary'>
          Sign up
        </Link>
      </div> */}
    </form>
  )
}
